import React, { useState, useEffect, useMemo } from "react";
import { createSelector } from "reselect";
import { useSelector } from "react-redux";
import * as XLSX from "xlsx";
import { toast } from "sonner";

import {
  ChevronLeft,
  Building,
  MapPin,
  GraduationCap,
  Folder,
  RefreshCw,
  User,
  Phone,
  Mail,
  Calendar,
  FileText,
  Shield,
  StickyNote,
  Edit,
  Trash2,
  Plus,
  Download,
  Clock,
  MessageSquare,
  Building2,
  Users,
  UserCheck,
  Search,
  Filter,
} from "lucide-react";

import { IoIosCloseCircle } from "react-icons/io";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import {
  useGetCategoriesByAcademyQuery,
  useCreateVendorCategoryMutation,
  useUpdateVendorCategoryMutation,
  useDeleteVendorCategoryMutation,
  useGetVendorsByAcademyQuery,
  useCreateVendorMutation,
  useUpdateVendorMutation,
  useDeleteVendorMutation,
  useGetVisitLogsByVendorQuery,
  useCreateVisitLogMutation,
  useUpdateVisitLogMutation,
  useDeleteVisitLogMutation,
  useCheckVendorIdentityMutation,
} from "../../slices/vendorApiSlice";
import { useGetUserByEmailQuery } from "../../slices/userApiSlice";
import CustomSelect from "../../components/CustomSelect/CustomSelect";

const VendorManagement = () => {
  const [currentView, setCurrentView] = useState("vendorList");
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [selectedLog, setSelectedLog] = useState(null);
  const [showCategories, setShowCategories] = useState(false);
  const [deleteCategoryConfirm, setDeleteCategoryConfirm] = useState({
    open: false,
    id: null,
  });
  const [deleteVendorConfirm, setDeleteVendorConfirm] = useState({
    open: false,
    id: null,
  });
  const [deleteLogConfirm, setDeleteLogConfirm] = useState({
    open: false,
    vendorId: null,
    logId: null,
  });

  const selectUserInfo = (state) => state.auth.userInfo || {};
  const getUserEmail = createSelector(
    selectUserInfo,
    (userInfo) => userInfo?.username || "",
  );
  const email = useSelector(getUserEmail);
  const { data: userByEmial } = useGetUserByEmailQuery(email);

  const academyId = userByEmial?.user?.academyId;

  // API Queries
  const {
    data: categories = [],
    isLoading: categoriesLoading,
    refetch: refetchCategories,
  } = useGetCategoriesByAcademyQuery(academyId, {
    skip: !academyId,
  });

  const {
    data: vendors = [],
    isLoading: vendorsLoading,
    refetch: refetchVendors,
  } = useGetVendorsByAcademyQuery(academyId, {
    skip: !academyId,
  });

  // API Mutations
  const [createVendorCategory] = useCreateVendorCategoryMutation();
  const [updateVendorCategory] = useUpdateVendorCategoryMutation();
  const [deleteVendorCategory] = useDeleteVendorCategoryMutation();
  const [createVendor] = useCreateVendorMutation();
  const [updateVendor] = useUpdateVendorMutation();
  const [deleteVendor] = useDeleteVendorMutation();
  const [createVisitLog] = useCreateVisitLogMutation();
  const [updateVisitLog] = useUpdateVisitLogMutation();
  const [deleteVisitLog] = useDeleteVisitLogMutation();

  const [showAddCategoryModal, setShowAddCategoryModal] = useState(false);
  const [showEditCategoryModal, setShowEditCategoryModal] = useState(false);
  const [newCategory, setNewCategory] = useState({
    title: "",
    description: "",
  });
  const [editCategory, setEditCategory] = useState({
    id: null,
    title: "",
    description: "",
  });

  const getCategoryColor = (categoryTitle) => {
    if (!categoryTitle) return "bg-gray-100 text-gray-700";

    const category = categories.find(
      (cat) =>
        cat.title === categoryTitle ||
        cat.name === categoryTitle ||
        cat.categoryName === categoryTitle,
    );

    return category?.color || "bg-gray-100 text-gray-700";
  };

  const generateColorFromTitle = (title) => {
    if (!title) return "bg-gray-100 text-gray-700";

    const colorMap = {
      maintenance: "bg-blue-100 text-blue-700",
      construction: "bg-green-100 text-green-700",
      "it services": "bg-purple-100 text-purple-700",
      cleaning: "bg-orange-100 text-orange-700",
      security: "bg-red-100 text-red-700",
      electrical: "bg-yellow-100 text-yellow-700",
      plumbing: "bg-teal-100 text-teal-700",
    };

    const normalizedTitle = title.toLowerCase().trim();
    return colorMap[normalizedTitle] || "bg-gray-100 text-gray-700";
  };

  const getRandomColor = (usedColors = []) => {
    const allColors = [
      "bg-blue-100 text-blue-700",
      "bg-green-100 text-green-700",
      "bg-purple-100 text-purple-700",
      "bg-orange-100 text-orange-700",
      "bg-red-100 text-red-700",
      "bg-yellow-100 text-yellow-700",
      "bg-indigo-100 text-indigo-700",
      "bg-pink-100 text-pink-700",
      "bg-teal-100 text-teal-700",
      "bg-cyan-100 text-cyan-700",
      "bg-amber-100 text-amber-700",
      "bg-lime-100 text-lime-700",
      "bg-emerald-100 text-emerald-700",
      "bg-violet-100 text-violet-700",
    ];

    const availableColors = allColors.filter(
      (color) => !usedColors.includes(color),
    );
    if (availableColors.length === 0) {
      return allColors[Math.floor(Math.random() * allColors.length)];
    }
    return availableColors[Math.floor(Math.random() * availableColors.length)];
  };

  // Export Vendor List to Excel
  const exportVendorListToExcel = () => {
    try {
      if (!vendors || vendors.length === 0) {
        toast.warning("No Data", {
          description: "There are no vendors to export.",
        });
        return;
      }

      if (categoriesLoading) {
        toast.info("Loading Data", {
          description: "Please wait while we load categories...",
        });
        return;
      }
      const vendorData = vendors.map((vendor) => {
        const getCategoryTitle = (vendor) => {
          if (vendor.vendorCategory && vendor.vendorCategory.title) {
            return vendor.vendorCategory.title;
          }
          if (vendor.category) {
            return vendor.category;
          }
          if (vendor.vendorCategoryId) {
            const category = categories.find(
              (cat) => cat.id === vendor.vendorCategoryId,
            );
            return category ? category.title : "Uncategorized";
          }
          return "Uncategorized";
        };

        const formatDate = (dateString) => {
          if (!dateString) return "-";
          try {
            const date = new Date(dateString);
            return date.toLocaleDateString("en-US", {
              year: "numeric",
              month: "short",
              day: "numeric",
            });
          } catch (error) {
            return dateString;
          }
        };

        const visitCount = vendor.visitLogs?.length || vendor.logs?.length || 0;

        return {
          "Vendor Name": vendor.name || "-",
          "Vendor ID": vendor.vendorIdentity || "-",
          Address: vendor.address || "-",
          "Contact Person":
            vendor.contactPersonName || vendor.contactPerson || "-",
          "Phone Number":
            vendor.contactPersonPhoneNumber || vendor.phoneNumber || "-",
          Email: vendor.contactPersonEmail || vendor.email || "-",
          Category: getCategoryTitle(vendor),
          "Contract Start": formatDate(
            vendor.contractStartDate || vendor.contractStart,
          ),
          "Contract End": formatDate(
            vendor.contractEndDate || vendor.contractEnd,
          ),
          "Visit Frequency Per Year":
            vendor.visitFrequencyPerYear || vendor.visitFrequency || "0",
          "BG Serial Number": vendor.bgSerialNumber || "-",
          "BG Expiry": formatDate(vendor.bgExpiryDate || vendor.bgExpiry),
          Note: vendor.note || "-",
        };
      });

      const exportData = [];
      exportData.push(["LIST OF REGISTERED VENDORS"]);
      exportData.push([]);
      exportData.push(["Report Date:", new Date().toLocaleDateString()]);
      exportData.push(["Total Vendors:", vendors.length]);
      exportData.push([]);
      exportData.push([]);

      const headers = Object.keys(vendorData[0]);
      exportData.push(headers);

      vendorData.forEach((vendor) => {
        const row = headers.map((header) => vendor[header]);
        exportData.push(row);
      });

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.aoa_to_sheet(exportData);
      XLSX.utils.book_append_sheet(wb, ws, "Registered Vendors");

      const fileName = `registered_vendors_list_${
        new Date().toISOString().split("T")[0]
      }.xlsx`;
      XLSX.writeFile(wb, fileName);

      toast.success(
        `Export Successful: List of registered vendors has been exported to ${fileName}`,
      );
    } catch (error) {
      console.error("Error exporting vendor list:", error);
      toast.error(
        error.message ||
          "Export Failed: Failed to export vendor list. Please try again.",
      );
    }
  };

  // Export function for visit logs
  const exportVendorLogsToExcel = (vendor, visitLogs) => {
    try {
      if (!vendor || !visitLogs || visitLogs.length === 0) {
        toast.warning("No Data: No visit logs available to export.");
        return;
      }

      const formatDate = (dateString) => {
        if (!dateString) return "-";
        try {
          const date = new Date(dateString);
          return date.toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
          });
        } catch (error) {
          return dateString;
        }
      };

      const formatTime = (timeString) => {
        if (!timeString) return "-";
        try {
          const timeWithoutSeconds = timeString
            .split(":")
            .slice(0, 2)
            .join(":");
          const [hours, minutes] = timeWithoutSeconds.split(":");
          const hour = parseInt(hours);
          const ampm = hour >= 12 ? "PM" : "AM";
          const displayHour = hour % 12 || 12;
          return `${displayHour}:${minutes.padStart(2, "0")} ${ampm}`;
        } catch (error) {
          return timeString;
        }
      };

      const exportData = [];
      exportData.push(["VENDOR VISIT LOGS REPORT"]);
      exportData.push([""]);
      exportData.push(["Vendor Information:"]);
      exportData.push(["Vendor Name:", vendor.name || "-"]);
      exportData.push(["Address:", vendor.address || "-"]);
      exportData.push(["Vendor ID:", vendor.vendorIdentity || "-"]);
      exportData.push([
        "Contact Person:",
        vendor.contactPersonName || vendor.contactPerson || "-",
      ]);
      exportData.push([
        "Phone Number:",
        vendor.contactPersonPhoneNumber || vendor.phoneNumber || "-",
      ]);
      exportData.push([
        "Email:",
        vendor.contactPersonEmail || vendor.email || "-",
      ]);
      exportData.push([
        "Category:",
        vendor.vendorCategory?.title || vendor.category || "Uncategorized",
      ]);
      exportData.push([
        "Contract Start:",
        formatDate(vendor.contractStartDate || vendor.contractStart),
      ]);
      exportData.push([
        "Contract End:",
        formatDate(vendor.contractEndDate || vendor.contractEnd),
      ]);
      exportData.push([
        "Visit Frequency:",
        (vendor.visitFrequencyPerYear || vendor.visitFrequency || "0") +
          " per year",
      ]);
      exportData.push(["BG Serial Number:", vendor.bgSerialNumber || "-"]);
      exportData.push([
        "BG Expiry:",
        formatDate(vendor.bgExpiryDate || vendor.bgExpiry),
      ]);
      exportData.push(["Note:", vendor.note || "-"]);
      exportData.push([""]);
      exportData.push(["Report Summary:"]);
      exportData.push(["Total Visit Logs:", visitLogs.length]);
      exportData.push(["Export Date:", new Date().toLocaleDateString()]);
      exportData.push([""]);
      exportData.push([""]);

      exportData.push([
        "Visit Date",
        "Start Time",
        "End Time",
        "Purpose",
        "Description",
        "Supervisor Comment",
        "Logged By",
        "Logged Date",
      ]);

      visitLogs.forEach((log) => {
        exportData.push([
          formatDate(log.visitDate),
          formatTime(log.startTime),
          formatTime(log.endTime),
          log.purpose || "-",
          log.log || log.description || "-",
          log.comment || log.supervisorComment || "-",
          log.loggedByName || log.loggedBy || "-",
          formatDate(log.createdAt),
        ]);
      });

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.aoa_to_sheet(exportData);

      ws["!cols"] = [
        { wch: 12 },
        { wch: 10 },
        { wch: 10 },
        { wch: 25 },
        { wch: 40 },
        { wch: 40 },
        { wch: 20 },
        { wch: 12 },
      ];

      XLSX.utils.book_append_sheet(wb, ws, "Vendor Visit Logs");

      const fileName = `${vendor.name.replace(
        /[^a-zA-Z0-9]/g,
        "_",
      )}_visit_logs_report_${new Date().toISOString().split("T")[0]}.xlsx`;
      XLSX.writeFile(wb, fileName);

      toast.success(
        `Export Successful: Visit logs report for ${vendor.name} has been exported to Excel`,
      );
    } catch (error) {
      console.error("Error exporting vendor logs:", error);
      toast.error(
        error.message ||
          "Export Failed: Failed to export visit logs. Please try again.",
      );
    }
  };

  // Category Management Functions
  const handleAddCategory = async () => {
    if (!newCategory.title || !newCategory.description) {
      toast.error("Please fill in both title and description");
      return;
    }

    if (!academyId) {
      toast.error("Unable to determine academy. Please check your account.");
      return;
    }

    try {
      const usedColors = categories.map((cat) => cat.color);
      const randomColor = getRandomColor(usedColors);

      const categoryData = {
        title: newCategory.title,
        description: newCategory.description,
        color: randomColor,
        academyId: academyId,
      };

      await createVendorCategory({
        academyId: academyId,
        vendorCategory: categoryData,
      }).unwrap();

      setNewCategory({ title: "", description: "" });
      setShowAddCategoryModal(false);
      refetchCategories();

      toast.success("Category added successfully");
    } catch (error) {
      console.error("Error adding category:", error);
      toast.error(
        error.data?.message || "Failed to add category. Please try again.",
      );
    }
  };

  const handleEditClick = (category) => {
    setEditCategory(category);
    setShowEditCategoryModal(true);
  };

  const handleUpdateCategory = async () => {
    if (!editCategory.title || !editCategory.description) {
      toast.error("Please fill in both title and description");
      return;
    }

    if (!academyId) {
      toast.error("Unable to determine academy. Please check your account.");
      return;
    }

    try {
      const updatedCategory = {
        ...editCategory,
        color: editCategory.color,
        academyId: academyId,
      };

      await updateVendorCategory({
        categoryId: editCategory.id,
        vendorCategory: updatedCategory,
      }).unwrap();

      setEditCategory({ id: null, title: "", description: "", color: "" });
      setShowEditCategoryModal(false);
      refetchCategories();

      toast.success("Category updated successfully");
    } catch (error) {
      console.error("Error updating category:", error);
      toast.error(
        error.data?.message || "Failed to update category. Please try again.",
      );
    }
  };

  const handleDeleteCategory = async (id) => {
    setDeleteCategoryConfirm({ open: true, id });
  };

  const confirmDeleteCategory = async () => {
    try {
      await deleteVendorCategory(deleteCategoryConfirm.id).unwrap();
      toast.success("Category has been deleted");
      setDeleteCategoryConfirm({ open: false, id: null });
    } catch (error) {
      toast.error(error.data?.message || "Failed to delete category");
    }
  };

  // Navigation Functions
  const handleBackToVendorList = () => {
    setCurrentView("vendorList");
    setSelectedVendor(null);
  };

  const handleViewVendorDetails = (vendor) => {
    setSelectedVendor(vendor);
    setCurrentView("vendorDetails");
  };

  const handleAddNewVendor = () => {
    setCurrentView("addVendor");
    setSelectedVendor(null);
  };

  const handleAddNewLog = () => {
    setCurrentView("addLog");
  };

  const handleEditExistingLog = (log) => {
    setSelectedLog(log);
    setCurrentView("logPreview");
  };

  const handleAddVendor = async (vendorData) => {
    try {
      const selectedCategory = categories.find(
        (cat) => cat.title === vendorData.category,
      );

      if (!selectedCategory) {
        toast.error("Please select a valid category");
        return;
      }

      const vendorToCreate = {
        vendorCategoryId: selectedCategory.id,
        academyId: academyId,
        name: vendorData.name,
        vendorIdentity: vendorData.vendorIdentity,
        address: vendorData.address,
        visitFrequencyPerYear: parseInt(vendorData.visitFrequency) || 0,
        contractStartDate: vendorData.contractStart,
        contractEndDate: vendorData.contractEnd,
        bgSerialNumber: vendorData.bgSerialNumber || null,
        bgExpiryDate: vendorData.bgExpiry || null,
        contactPersonName: vendorData.contactPerson,
        contactPersonPhoneNumber: vendorData.phoneNumber,
        contactPersonEmail: vendorData.email,
        note: vendorData.note,
      };

      await createVendor(vendorToCreate).unwrap();
      refetchVendors();

      toast.success("Vendor added successfully");
      setCurrentView("vendorList");
    } catch (error) {
      console.error("Error adding vendor:", error);
      toast.error(
        error.data?.message || "Failed to add vendor. Please try again.",
      );
    }
  };

  const handleEditVendor = async (updatedVendor) => {
    try {
      const selectedCategory = categories.find(
        (cat) => cat.title === updatedVendor.category,
      );

      if (!selectedCategory) {
        toast.error("Please select a valid category");
        return;
      }

      const vendorToUpdate = {
        vendorCategoryId: selectedCategory.id,
        academyId: academyId,
        name: updatedVendor.name,
        vendorIdentity: updatedVendor.vendorIdentity,
        address: updatedVendor.address,
        visitFrequencyPerYear: parseInt(updatedVendor.visitFrequency) || 0,
        contractStartDate: updatedVendor.contractStart,
        contractEndDate: updatedVendor.contractEnd,
        bgSerialNumber: updatedVendor.bgSerialNumber,
        bgExpiryDate: updatedVendor.bgExpiry,
        contactPersonName: updatedVendor.contactPerson,
        contactPersonPhoneNumber: updatedVendor.phoneNumber,
        contactPersonEmail: updatedVendor.email,
        note: updatedVendor.note,
      };

      await updateVendor({
        vendorId: updatedVendor.id,
        vendorData: vendorToUpdate,
      }).unwrap();
      refetchVendors();

      toast.success("Vendor updated successfully");
      setCurrentView("vendorDetails");
      const updatedVendorData = {
        ...updatedVendor,
        vendorCategory: selectedCategory,
      };
      setSelectedVendor(updatedVendorData);
    } catch (error) {
      console.error("Error updating vendor:", error);
      toast.error(
        error.data?.message || "Failed to update vendor. Please try again.",
      );
    }
  };

  const handleDeleteVendor = async (vendorId) => {
    setDeleteVendorConfirm({ open: true, id: vendorId });
  };

  const confirmDeleteVendor = async () => {
    try {
      await deleteVendor(deleteVendorConfirm.id).unwrap();
      setCurrentView("vendorList");
      toast.success("Vendor has been deleted");
      setDeleteVendorConfirm({ open: false, id: null });
    } catch (error) {
      toast.error(error.data?.message || "Failed to delete vendor");
    }
  };

  const handleAddLog = async (vendorId, logData) => {
    try {
      const visitLogData = {
        visitDate: logData.visitDate,
        startTime: logData.startTime.includes(":")
          ? logData.startTime
          : `${logData.startTime}:00`,
        endTime: logData.endTime.includes(":")
          ? logData.endTime
          : `${logData.endTime}:00`,
        purpose: logData.purpose,
        log: logData.description,
        comment: logData.supervisorComment,
        loggedBy: email,
        loggedByName: userByEmial?.user?.name || "User",
      };

      await createVisitLog({
        vendorId: vendorId,
        visitLog: visitLogData,
      }).unwrap();

      refetchVendors();

      toast.success("Log added successfully");
      setCurrentView("vendorDetails");
    } catch (error) {
      console.error("Error adding visit log:", error);
      toast.error(
        error.data?.message || "Failed to add log. Please try again.",
      );
    }
  };

  const handleEditLog = async (vendorId, updatedLog) => {
    try {
      const visitLogData = {
        visitDate: updatedLog.visitDate,
        startTime: updatedLog.startTime.includes(":")
          ? updatedLog.startTime
          : `${updatedLog.startTime}:00`,
        endTime: updatedLog.endTime.includes(":")
          ? updatedLog.endTime
          : `${updatedLog.endTime}:00`,
        purpose: updatedLog.purpose,
        log: updatedLog.description,
        comment: updatedLog.supervisorComment,
        loggedBy: email,
        loggedByName:
          updatedLog.loggedByName || userByEmial?.user?.name || "User",
      };

      await updateVisitLog({
        visitLogId: updatedLog.id,
        visitLog: visitLogData,
      }).unwrap();

      refetchVendors();

      toast.success("Log updated successfully");
      setCurrentView("vendorDetails");
      setSelectedLog(null);
    } catch (error) {
      console.error("Error updating visit log:", error);
      toast.error(
        error.data?.message || "Failed to update log. Please try again.",
      );
    }
  };

  const handleDeleteLog = async (vendorId, logId) => {
    const promise = deleteVisitLog(logId).unwrap();
    toast.promise(promise, {
      loading: "Deleting log...",
      success: "Log has been deleted",
      error: (err) => err.data?.message || "Failed to delete log",
    });
    try {
      await promise;
    } catch (error) {
      console.error("Error deleting log:", error);
    }
  };

  const confirmDeleteLog = async () => {
    try {
      if (deleteLogConfirm.onDelete) {
        await deleteLogConfirm.onDelete(
          deleteLogConfirm.vendorId,
          deleteLogConfirm.logId,
        );
      }
      if (deleteLogConfirm.onBack) {
        deleteLogConfirm.onBack();
      }
      setDeleteLogConfirm({
        open: false,
        vendorId: null,
        logId: null,
        onDelete: null,
        onBack: null,
      });
    } catch (error) {
      console.error("Delete failed:", error);
      toast.error("Failed to delete log");
    }
  };

  const renderCurrentView = () => {
    if (!academyId) {
      return (
        <div className="bg-white p-4 flex items-center justify-center">
          <div className="text-center">
            <p className="text-gray-600">
              Unable to determine academy. Please check your account.
            </p>
            <p className="text-sm text-gray-500 mt-2">
              Academy ID: {academyId || "Not found"}
            </p>
          </div>
        </div>
      );
    }

    if (categoriesLoading || vendorsLoading) {
      return (
        <div className="bg-white p-4 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black mx-auto"></div>
            <p className="mt-4 text-gray-600">
              {categoriesLoading
                ? "Loading categories..."
                : "Loading vendors..."}
            </p>
          </div>
        </div>
      );
    }

    if (showCategories) {
      return (
        <div className="p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowCategories(false)}
                className=" md:flex w-8 h-8 sm:w-9 sm:h-9 items-center justify-center rounded-full hover:bg-gray-100 hover:border-gray-400 transition-all"
                aria-hidden={true}
              >
                <ChevronLeft
                  size={24}
                  className="text-gray-700 sm:w-5 sm:h-5"
                />
              </button>
              <div>
                <h2 className="text-xl sm:text-2xl font-bold">
                  Vendor Categories
                </h2>
                <p className="text-sm text-gray-500">
                  Manage your vendor categories
                </p>
              </div>
            </div>
            <button
              onClick={() => setShowAddCategoryModal(true)}
              className="px-4 py-2 bg-black text-white rounded-lg shadow-sm flex items-center justify-center gap-2 whitespace-nowrap text-sm sm:text-base"
              style={{
                backgroundColor: "rgb(48 88 69 / var(--tw-bg-opacity, 1))",
              }}
            >
              <Plus size={16} />
              Add Category
            </button>
          </div>
          {/* In the showCategories section */}
          <div className="space-y-4">
            {[...categories]
              .sort((a, b) => {
                // Sort by creation date (newest first)
                // Use createdAt if available, otherwise fallback to id or title
                const dateA = new Date(
                  a.createdAt || a.createdDate || a.id || a.title,
                );
                const dateB = new Date(
                  b.createdAt || b.createdDate || b.id || b.title,
                );
                return dateB - dateA; // Newest first
              })
              .map((c) => (
                <div
                  key={c.id}
                  className="bg-white rounded-xl shadow-sm p-4 sm:p-6 border border-gray-200"
                >
                  {/* Existing category card content */}
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span
                          className={`px-3 py-1 rounded-full text-sm font-medium ${generateColorFromTitle(
                            c.title,
                          )}`}
                        >
                          {c.title}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600">{c.description}</p>
                    </div>
                    <div className="flex gap-2 justify-end">
                      <button
                        className="px-3 sm:px-4 py-2 bg-white text-black border border-gray-300 rounded-lg shadow-sm hover:bg-gray-50 transition-colors flex items-center gap-2 text-sm"
                        onClick={() => handleEditClick(c)}
                      >
                        <Edit size={16} />
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteCategory(c.id)}
                        className="px-3 sm:px-4 py-2 bg-white text-red-600 border border-gray-300 rounded-lg shadow-sm hover:bg-red-50 transition-colors flex items-center gap-2 text-sm"
                      >
                        <Trash2 size={16} />
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
          </div>
        </div>
      );
    }

    switch (currentView) {
      case "vendorList":
        return (
          <VendorList
            vendors={vendors}
            onViewVendor={handleViewVendorDetails}
            onAddVendor={handleAddNewVendor}
            onDeleteVendor={handleDeleteVendor}
            onShowCategories={() => setShowCategories(true)}
            getCategoryColor={getCategoryColor}
            categories={categories}
            onExportVendorList={exportVendorListToExcel}
          />
        );
      case "vendorDetails":
        return (
          <VendorDetails
            vendor={selectedVendor}
            onEditVendor={() => setCurrentView("editVendor")}
            onBack={handleBackToVendorList}
            onAddLog={handleAddNewLog}
            onEditLog={handleEditExistingLog}
            onDeleteLog={handleDeleteLog}
            categories={categories}
            onExportVendorLogs={exportVendorLogsToExcel}
            setDeleteLogConfirm={setDeleteLogConfirm}
          />
        );
      case "addLog":
        return (
          <LogForm
            vendor={selectedVendor}
            onSave={handleAddLog}
            onCancel={() => setCurrentView("vendorDetails")}
            mode="add"
          />
        );
      case "editLog":
        return (
          <LogForm
            vendor={selectedVendor}
            log={selectedLog}
            onSave={handleEditLog}
            onCancel={() => setCurrentView("vendorDetails")}
            mode="edit"
          />
        );
      case "addVendor":
        return (
          <VendorMngt
            onAddVendor={handleAddVendor}
            onCancel={handleBackToVendorList}
            categories={categories}
            academyId={academyId}
          />
        );
      case "editVendor":
        return (
          <VendorMngt
            onAddVendor={handleEditVendor}
            onCancel={() => setCurrentView("vendorDetails")}
            categories={categories}
            editVendor={selectedVendor}
            academyId={academyId}
          />
        );
      case "logPreview":
        return (
          <LogPreviewPage
            log={selectedLog}
            vendor={selectedVendor}
            onBack={() => setCurrentView("vendorDetails")}
            onEdit={(log) => {
              setSelectedLog(log);
              setCurrentView("editLog");
            }}
            onDelete={handleDeleteLog}
            setDeleteLogConfirm={setDeleteLogConfirm}
          />
        );
      default:
        return (
          <VendorList
            vendors={vendors}
            onViewVendor={handleViewVendorDetails}
            onAddVendor={handleAddNewVendor}
            onDeleteVendor={handleDeleteVendor}
            onShowCategories={() => setShowCategories(true)}
            getCategoryColor={getCategoryColor}
            categories={categories}
          />
        );
    }
  };

  return (
    <div className="h-full bg-white">
      {renderCurrentView()}

      {/* Add Category Modal */}
      {showAddCategoryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
          <div className="bg-white p-6 sm:p-8 rounded-2xl shadow-md w-full max-w-md relative">
            <div className="text-center mb-6">
              <h2 className="text-xl sm:text-2xl font-bold">Add Category</h2>
              <p className="text-gray-500 text-sm sm:text-base">
                Add categories here and the descriptions
              </p>
            </div>
            <button
              onClick={() => setShowAddCategoryModal(false)}
              className="absolute top-4 right-4"
            >
              <IoIosCloseCircle
                style={{ color: "#897463", width: "24px", height: "24px" }}
              />
            </button>

            <div className="flex flex-col gap-4">
              <div>
                <label className="block mb-1 font-medium text-sm sm:text-base">
                  Category Title
                </label>
                <input
                  type="text"
                  placeholder="Category"
                  value={newCategory.title}
                  onChange={(e) =>
                    setNewCategory({ ...newCategory, title: e.target.value })
                  }
                  className="w-full border border-gray-300 rounded-lg p-3 text-sm sm:text-base"
                  required
                />
              </div>

              <div>
                <label className="block mb-1 font-medium text-sm sm:text-base">
                  Description
                </label>
                <textarea
                  placeholder="Description of the category"
                  value={newCategory.description}
                  onChange={(e) =>
                    setNewCategory({
                      ...newCategory,
                      description: e.target.value,
                    })
                  }
                  className="w-full border border-gray-300 rounded-lg p-3 text-sm sm:text-base"
                  rows={4}
                  required
                />
              </div>

              <div className="flex justify-center gap-3 mt-4">
                <button
                  onClick={() => setShowAddCategoryModal(false)}
                  className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors text-sm sm:text-base"
                >
                  Cancel
                </button>
                <button
                  className="px-4 py-2 bg-black text-white rounded-lg shadow-sm text-sm sm:text-base"
                  onClick={handleAddCategory}
                >
                  Add
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Category Modal */}
      {showEditCategoryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
          <div className="bg-white p-6 sm:p-8 rounded-2xl shadow-md w-full max-w-md relative">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl sm:text-2xl font-bold">Edit Category</h2>
              <button onClick={() => setShowEditCategoryModal(false)}>
                <IoIosCloseCircle
                  style={{ color: "#897463", width: "24px", height: "24px" }}
                />
              </button>
            </div>

            <div className="flex flex-col gap-4">
              <div>
                <label className="block mb-1 font-medium text-sm sm:text-base">
                  Category Title
                </label>
                <input
                  type="text"
                  placeholder="Category Title"
                  value={editCategory.title}
                  onChange={(e) =>
                    setEditCategory({ ...editCategory, title: e.target.value })
                  }
                  className="w-full border border-gray-300 rounded-lg p-3 text-sm sm:text-base"
                  required
                />
              </div>

              <div>
                <label className="block mb-1 font-medium text-sm sm:text-base">
                  Description
                </label>
                <textarea
                  placeholder="Description"
                  value={editCategory.description}
                  onChange={(e) =>
                    setEditCategory({
                      ...editCategory,
                      description: e.target.value,
                    })
                  }
                  className="w-full border border-gray-300 rounded-lg p-3 text-sm sm:text-base"
                  rows={4}
                  required
                />
              </div>

              <button
                className="px-4 py-2 bg-black text-white rounded-lg shadow-sm mt-4 text-sm sm:text-base"
                style={{
                  backgroundColor: "rgb(48 88 69 / var(--tw-bg-opacity, 1))",
                  color: "white",
                }}
                onClick={handleUpdateCategory}
              >
                Update
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Category Confirmation Dialog */}
      <AlertDialog
        open={deleteCategoryConfirm.open}
        onOpenChange={(open) =>
          !open && setDeleteCategoryConfirm({ open: false, id: null })
        }
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Category?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this category? This action cannot
              be undone and the category will be permanently removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() =>
                setDeleteCategoryConfirm({ open: false, id: null })
              }
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteCategory}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Vendor Confirmation Dialog */}
      <AlertDialog
        open={deleteVendorConfirm.open}
        onOpenChange={(open) =>
          !open && setDeleteVendorConfirm({ open: false, id: null })
        }
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Vendor?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this vendor? This action cannot be
              undone and all vendor data will be permanently removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => setDeleteVendorConfirm({ open: false, id: null })}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteVendor}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Log Confirmation Dialog */}
      <AlertDialog
        open={deleteLogConfirm.open}
        onOpenChange={(open) =>
          !open &&
          setDeleteLogConfirm({
            open: false,
            vendorId: null,
            logId: null,
            onDelete: null,
            onBack: null,
          })
        }
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Visit Log?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this visit log? This action cannot
              be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() =>
                setDeleteLogConfirm({
                  open: false,
                  vendorId: null,
                  logId: null,
                  onDelete: null,
                  onBack: null,
                })
              }
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteLog}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

const VendorList = ({
  vendors,
  onViewVendor,
  onAddVendor,
  onDeleteVendor,
  onShowCategories,
  getCategoryColor,
  categories,
  onExportVendorList,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");

  // Calculate statistics
  const totalVendors = vendors.length;

  const activeVendors = vendors.filter((vendor) => {
    const contractEndDate = vendor.contractEndDate || vendor.contractEnd;
    if (!contractEndDate) return false;

    const endDate = new Date(contractEndDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return endDate >= today;
  }).length;

  // UPDATED VisitCount component to handle different data structures
  const VisitCount = ({ vendorId, vendor }) => {
    const { data: visitLogs, isLoading } = useGetVisitLogsByVendorQuery(
      vendorId,
      {
        skip: !vendorId,
      },
    );

    // FIXED: Handle all possible data structures for visit logs
    let vendorLogs = [];

    // Priority 1: Use direct vendor data if available
    if (vendor?.visitLogs && Array.isArray(vendor.visitLogs)) {
      vendorLogs = vendor.visitLogs;
    } else if (vendor?.logs && Array.isArray(vendor.logs)) {
      vendorLogs = vendor.logs;
    }
    // Priority 2: Use query result if vendor data doesn't have logs
    else if (visitLogs) {
      if (Array.isArray(visitLogs)) {
        vendorLogs = visitLogs;
      } else if (visitLogs.visitLogs && Array.isArray(visitLogs.visitLogs)) {
        vendorLogs = visitLogs.visitLogs;
      } else if (visitLogs.data && Array.isArray(visitLogs.data)) {
        vendorLogs = visitLogs.data;
      }
    }

    const visitCount = vendorLogs.length;

    if (isLoading) {
      return (
        <span className="inline-flex px-2.5 py-1 rounded-md bg-gray-50 text-gray-600 text-xs font-medium border border-gray-200">
          Loading...
        </span>
      );
    }

    return (
      <span className="inline-flex px-2.5 py-1 rounded-md text-xs font-medium">
        {visitCount} {visitCount === 1 ? "" : ""}
      </span>
    );
  };

  const handleDeleteVendor = (vendor, e) => {
    e.stopPropagation();
    onDeleteVendor(vendor.id);
  };

  const getVendorCategoryTitle = (vendor) => {
    if (!vendor) return "Uncategorized";

    if (vendor.vendorCategory && vendor.vendorCategory.title) {
      return vendor.vendorCategory.title;
    }
    if (vendor.category) {
      return vendor.category;
    }
    if (vendor.vendorCategoryId) {
      const category = categories.find(
        (cat) => cat.id === vendor.vendorCategoryId,
      );
      return category ? category.title : "Uncategorized";
    }
    return "Uncategorized";
  };

  const getVendorCategoryForFilter = (vendor) => {
    const categoryTitle = getVendorCategoryTitle(vendor);
    return categoryTitle.toLowerCase().trim();
  };

  // Prepare category options for CustomSelect
  const categoryOptions = useMemo(() => {
    return categories.map((category) => ({
      value: category.title,
      label: category.title,
      id: category.id,
    }));
  }, [categories]);

  // Get category display name
  const getCategoryDisplayName = (categoryValue) => {
    if (categoryValue === "all" || !categoryValue) return "Category";
    return categoryValue;
  };

  // Sort vendors by creation date (newest first)
  const sortedVendors = useMemo(() => {
    return [...vendors].sort((a, b) => {
      const dateA = new Date(a.createdAt || a.updatedAt || a.id || 0);
      const dateB = new Date(b.createdAt || b.updatedAt || b.id || 0);
      return dateB - dateA; // Newest first
    });
  }, [vendors]);

  // Filter the sorted vendors
  const filteredVendors = useMemo(() => {
    return sortedVendors.filter((vendor) => {
      const matchesSearch = vendor.name
        .toLowerCase()
        .includes(searchTerm.toLowerCase());

      const vendorCategory = getVendorCategoryForFilter(vendor);
      const selectedCategoryLower = selectedCategory.toLowerCase().trim();
      const matchesCategory =
        selectedCategory === "all" ||
        selectedCategory === "" ||
        vendorCategory === selectedCategoryLower;

      return matchesSearch && matchesCategory;
    });
  }, [sortedVendors, searchTerm, selectedCategory]);

  const isVendorActive = (vendor) => {
    const contractEndDate = vendor.contractEndDate || vendor.contractEnd;
    if (!contractEndDate) return false;

    const endDate = new Date(contractEndDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return endDate >= today;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch (error) {
      return dateString;
    }
  };

  return (
    <div className="flex flex-col h-screen bg-background">
      {/* Statistics Cards */}
      <div className="border-b bg-card px-6 py-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl">
          <div className="bg-card border rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-muted rounded-lg">
                <Users size={20} className="text-foreground" />
              </div>
              <div>
                <p className="text-2xl font-semibold">{totalVendors}</p>
                <h3 className="text-sm text-muted-foreground">Total Vendors</h3>
              </div>
            </div>
          </div>

          <div className="bg-card border rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-muted rounded-lg">
                <Clock size={20} className="text-foreground" />
              </div>
              <div>
                <p className="text-2xl font-semibold">{activeVendors}</p>
                <h3 className="text-sm text-muted-foreground">
                  Active Vendors
                </h3>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Header with Search and Filters */}
      <div className="border-b bg-card px-6 py-4">
        <h3 className="text-lg font-semibold mb-4">Vendor Management</h3>
        <div className="flex flex-col md:flex-row gap-3">
          <div className="relative flex-1 md:max-w-sm">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search vendors..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <CustomSelect
            options={categoryOptions}
            value={selectedCategory}
            onChange={setSelectedCategory}
            placeholder="Category"
            allOptionLabel="All Categories"
            getDisplayValue={getCategoryDisplayName}
            allValue="all"
            minWidth={180}
          />
          <Button
            onClick={onShowCategories}
            variant="outline"
            className="whitespace-nowrap"
          >
            <Folder className="h-4 w-4 mr-2" />
            Manage Categories
          </Button>
          <Button
            onClick={onExportVendorList}
            disabled={vendors.length === 0}
            variant="outline"
            className="whitespace-nowrap"
          >
            <Download className="h-4 w-4 mr-2" />
            {vendors.length === 0 ? "No Data to Export" : "Export"}
          </Button>
          <Button
            onClick={onAddVendor}
            className="whitespace-nowrap"
            style={{
              backgroundColor: "rgb(48 88 69 / var(--tw-bg-opacity, 1))",
            }}
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Vendor
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto p-6">
        <div className="rounded-md border bg-card">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="font-semibold">Vendor</TableHead>
                <TableHead className="font-semibold">Vendor ID</TableHead>
                <TableHead className="font-semibold">Address</TableHead>
                <TableHead className="font-semibold">Contact</TableHead>
                <TableHead className="font-semibold">Category</TableHead>
                <TableHead className="font-semibold">Status</TableHead>
                <TableHead className="font-semibold">Visits</TableHead>
                <TableHead className="font-semibold text-right">
                  Actions
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredVendors.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={8}
                    className="h-24 text-center text-muted-foreground"
                  >
                    {searchTerm || selectedCategory !== "all"
                      ? "No vendors found matching your criteria."
                      : "No vendors found."}
                  </TableCell>
                </TableRow>
              ) : (
                filteredVendors.map((vendor) => {
                  const vendorCategoryTitle = getVendorCategoryTitle(vendor);
                  const isActive = isVendorActive(vendor);

                  return (
                    <TableRow
                      key={vendor.id}
                      className="cursor-pointer"
                      onClick={() => onViewVendor(vendor)}
                    >
                      <TableCell className="font-medium">
                        {vendor.name}
                      </TableCell>
                      <TableCell>{vendor.vendorIdentity || "-"}</TableCell>
                      <TableCell>
                        <div className="max-w-xs truncate">
                          {vendor.address || "-"}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          <div className="font-medium">
                            {vendor.contactPersonName ||
                              vendor.contactPerson ||
                              "-"}
                          </div>
                          <div className="text-muted-foreground">
                            {vendor.contactPersonPhoneNumber ||
                              vendor.phoneNumber ||
                              "-"}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant="secondary"
                          className={getCategoryColor(vendorCategoryTitle)}
                        >
                          {vendorCategoryTitle}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={isActive ? "success" : "destructive"}>
                          {isActive ? "Active" : "Expired"}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <VisitCount vendorId={vendor.id} vendor={vendor} />
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => handleDeleteVendor(vendor, e)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
};

const LogPreviewPage = ({
  log,
  vendor,
  onBack,
  onEdit,
  onDelete,
  setDeleteLogConfirm,
}) => {
  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatTime = (timeString) => {
    if (!timeString) return "";
    const [hours, minutes] = timeString.split(":");
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? "PM" : "AM";
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const handleDelete = async (log) => {
    setDeleteLogConfirm({
      open: true,
      vendorId: vendor.id,
      logId: log.id,
      onDelete,
      onBack,
    });
  };

  return (
    <div className=" bg-white-100 p-4">
      <div className="max-w-4xl mx-auto">
        {/* Log Preview Content */}
        <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-6 border">
          <div className="space-y-4 sm:space-y-6">
            {/* Header with user info and action buttons */}
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center bg-gray-100 border-b-2 border-gray-200 pb-4 -mt-4 sm:-mt-6 -mx-4 sm:-mx-6 px-4 sm:px-6 pt-4 rounded-t-2xl gap-3 sm:gap-0">
              <div className="flex items-center justify-between mb-6">
                {/* Back Button */}
                <button
                  aria-label="Back"
                  onClick={onBack}
                  className="flex w-8 h-8 sm:w-10 sm:h-10 items-center justify-center rounded-full hover:bg-gray-200 transition-colors text-gray-700"
                >
                  <ChevronLeft size={24} className="sm:w-6 sm:h-6" />
                </button>

                {/* "Logged by" Text */}
                <div className="text-center flex-1">
                  <span className="font-medium text-sm sm:text-base">
                    <span className="text-gray-600">Logged by</span>
                    <span className="text-blue-500">
                      {" "}
                      {log.loggedBy || log.loggedByName || "User"}
                    </span>
                  </span>
                </div>
              </div>

              <div className="flex gap-2 self-end sm:self-auto">
                <button
                  onClick={() => onEdit(log)}
                  className="flex items-center gap-2 py-2 px-3 sm:px-4 bg-white text-black border border-gray-400 rounded-lg shadow-sm hover:bg-gray-100 whitespace-nowrap text-xs sm:text-sm"
                >
                  <Edit className="w-3 h-3 sm:w-4 sm:h-4" />
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(log)}
                  className="flex items-center gap-2 py-2 px-3 sm:px-4 bg-white text-red-600 border border-gray-400 rounded-lg shadow-sm hover:bg-red-50 text-xs sm:text-sm"
                >
                  <Trash2 className="w-3 h-3 sm:w-4 sm:h-4" />
                  Delete
                </button>
              </div>
            </div>

            {/* Purpose and Time */}
            <div className="border-b border-gray-200 pb-1">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start mb-2 gap-2 sm:gap-4">
                <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold flex-1 break-words min-w-0">
                  {log.purpose}
                </h2>
                <p className="text-gray-600 whitespace-nowrap flex-shrink-0 text-base sm:text-lg">
                  {formatTime(log.startTime)} – {formatTime(log.endTime)}
                </p>
              </div>

              <p className="text-gray-600 mb-4 sm:mb-6 text-base sm:text-lg">
                {formatDate(log.visitDate)}
              </p>
            </div>

            {/* Content */}
            <div className="space-y-4 sm:space-y-6">
              <div>
                <p className="text-[#555555] whitespace-pre-wrap text-lg leading-relaxed">
                  {log.log || log.description || "No description available"}
                </p>
              </div>

              {log.comment && (
                <div>
                  <h3 className="font-semibold text-[#ABABAB] mb-3 text-sm">
                    Supervisor Comment:
                  </h3>
                  <p className="text-[#555555] whitespace-pre-wrap text-lg leading-relaxed">
                    {log.comment}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const VendorDetails = ({
  vendor,
  onEditVendor,
  onBack,
  onAddLog,
  onEditLog,
  onDeleteLog,
  categories,
  onExportVendorLogs,
  setDeleteLogConfirm,
}) => {
  const [selectedLog, setSelectedLog] = useState(null);
  const [sortOrder, setSortOrder] = useState("latest"); // 'latest' or 'earliest'

  // Fetch visit logs for this vendor
  const {
    data: visitLogsData,
    isLoading: logsLoading,
    refetch: refetchLogs,
    error: logsError,
  } = useGetVisitLogsByVendorQuery(vendor?.id, {
    skip: !vendor?.id,
    refetchOnMountOrArgChange: true,
  });

  // Extract logs from response
  const visitLogs = visitLogsData?.visitLogs || visitLogsData || [];

  // Handle export visit logs
  const handleExportVisitLogs = () => {
    if (!visitLogs || visitLogs.length === 0) {
      toast.warning("No Data: No visit logs available to export.");
      return;
    }
    onExportVendorLogs(vendor, visitLogs);
  };

  // Sort the logs based on selected order
  const sortedLogs = [...visitLogs].sort((a, b) => {
    const dateA = new Date(a.visitDate + "T" + a.startTime);
    const dateB = new Date(b.visitDate + "T" + b.startTime);

    if (sortOrder === "latest") {
      return dateB - dateA; // Latest first (descending)
    } else {
      return dateA - dateB;
    }
  });

  // Handle sort change
  const handleSortChange = (e) => {
    setSortOrder(e.target.value);
  };

  const getVendorField = (fieldName) => {
    const fieldMap = {
      contactPerson: vendor?.contactPersonName || vendor?.contactPerson,
      phoneNumber: vendor?.contactPersonPhoneNumber || vendor?.phoneNumber,
      email: vendor?.contactPersonEmail || vendor?.email,
      contractStart: vendor?.contractStartDate || vendor?.contractStart,
      contractEnd: vendor?.contractEndDate || vendor?.contractEnd,
      bgSerialNumber: vendor?.bgSerialNumber,
      bgExpiry: vendor?.bgExpiryDate || vendor?.bgExpiry,
      visitFrequency: vendor?.visitFrequencyPerYear || vendor?.visitFrequency,
      name: vendor?.name,
      address: vendor?.address,
      note: vendor?.note,
      vendorIdentity: vendor?.vendorIdentity, // ADD THIS LINE
    };
    return fieldMap[fieldName] || "-";
  };

  // Get vendor category title
  const getVendorCategoryTitle = () => {
    if (vendor?.vendorCategory && vendor.vendorCategory.title) {
      return vendor.vendorCategory.title;
    }
    if (vendor?.category) {
      return vendor.category;
    }
    if (vendor?.vendorCategoryId) {
      const category = categories.find(
        (cat) => cat.id === vendor.vendorCategoryId,
      );
      return category ? category.title : "Uncategorized";
    }
    return "Uncategorized";
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch (error) {
      return dateString;
    }
  };

  const formatTime = (timeString) => {
    if (!timeString) return "";
    try {
      const timeWithoutSeconds = timeString.split(":").slice(0, 2).join(":");
      const [hours, minutes] = timeWithoutSeconds.split(":");
      const hour = parseInt(hours);
      const ampm = hour >= 12 ? "PM" : "AM";
      const displayHour = hour % 12 || 12;
      return `${displayHour}:${minutes.padStart(2, "0")} ${ampm}`;
    } catch (error) {
      return timeString;
    }
  };

  const handleDeleteLog = (log) => {
    setDeleteLogConfirm({
      open: true,
      vendorId: vendor.id,
      logId: log.id,
      onDeleteLog,
    });
  };

  const handleEditLogClick = (log) => {
    setSelectedLog(log);
    onEditLog(log);
  };

  if (!vendor) {
    return (
      <div className="bg-white p-6 rounded-2xl shadow">
        <div className="text-center py-8">
          <p>Vendor not found</p>
          <button
            onClick={onBack}
            className="mt-4 px-4 py-2 bg-black text-white rounded-lg"
          >
            Back to Vendor List
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className=" bg-white-100 p-4">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button
          aria-label="Back"
          onClick={onBack}
          className="md:flex w-8 h-8 sm:w-9 sm:h-9 items-center justify-center rounded-full hover:bg-gray-100 hover:border-gray-400 transition-all"
        >
          <ChevronLeft
            size={24}
            strokeWidth={2}
            className="text-gray-700 sm:w-6 sm:h-6"
          />
        </button>
        <div className="flex items-center gap-2 sm:gap-3">
          <Building2 className="w-6 h-6 sm:w-8 sm:h-8 text-gray-600" />
          <div>
            <h1 className="text-3xl font-bold">{getVendorField("name")}</h1>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
        {/* Left Side - Profile */}
        <div className="space-y-4">
          <div className="bg-white border p-4 sm:p-6 rounded-lg shadow">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-6 gap-3 sm:gap-0">
              <div>
                <h2 className="text-lg sm:text-xl font-bold">Profile</h2>
                <p className="text-gray-600 text-sm sm:text-base">
                  Company profile
                </p>
              </div>
              <button
                onClick={onEditVendor}
                className="w-full sm:w-auto px-4 py-2 bg-white border border-gray-300 rounded-lg shadow-sm flex items-center justify-center gap-1 whitespace-nowrap hover:bg-gray-50 text-sm sm:text-base"
              >
                <Edit size={16} />
                Edit Vendor
              </button>
            </div>

            {/* Company Details */}
            <div className="space-y-4 mb-6 sm:mb-8">
              <h3 className="font-semibold text-gray-600 text-sm sm:text-base mb-3">
                Company Details
              </h3>
              {[
                {
                  label: "Name",
                  value: getVendorField("name"),
                  icon: Building,
                },
                {
                  label: "Vendor Identity (TPN)",
                  value: getVendorField("vendorIdentity"),
                  icon: FileText,
                },
                {
                  label: "Address",
                  value: getVendorField("address"),
                  icon: MapPin,
                },
                {
                  label: "Category",
                  value: getVendorCategoryTitle(),
                  icon: Folder,
                },
                {
                  label: "Contract Start",
                  value: formatDate(getVendorField("contractStart")),
                  icon: Calendar,
                },
                {
                  label: "Contract End",
                  value: formatDate(getVendorField("contractEnd")),
                  icon: Calendar,
                },
                {
                  label: "Visit Frequency",
                  value: getVendorField("visitFrequency")
                    ? `${getVendorField("visitFrequency")} per year`
                    : "-",
                  icon: RefreshCw,
                },
              ].map((field, index) => (
                <div
                  key={index}
                  className="border-b border-gray-200 pb-3 last:border-b-0"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <field.icon className="w-5 h-5 text-white bg-[#8E8E93] p-1 rounded flex-shrink-0" />
                      <span className="text-sm font-medium text-gray-600">
                        {field.label}
                      </span>
                    </div>
                    <span className="text-gray-800 text-right">
                      {field.value || "-"}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            {/* Contact Details */}
            <div className="space-y-4 mb-6 sm:mb-8">
              <h3 className="font-semibold text-gray-600 text-sm sm:text-base mb-3">
                Contact Details
              </h3>
              {[
                {
                  label: "Contact Person",
                  value: getVendorField("contactPerson"),
                  icon: User,
                },
                {
                  label: "Phone Number",
                  value: getVendorField("phoneNumber"),
                  icon: Phone,
                },
                { label: "Email", value: getVendorField("email"), icon: Mail },
              ].map((field, index) => (
                <div
                  key={index}
                  className="border-b border-gray-200 pb-3 last:border-b-0"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <field.icon className="w-5 h-5 text-white bg-[#3776FB] p-1 rounded flex-shrink-0" />
                      <span className="text-sm font-medium text-gray-600">
                        {field.label}
                      </span>
                    </div>
                    <span className="text-gray-800 text-right">
                      {field.value || "-"}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            {/* Contract & Bank Details */}
            <div className="space-y-4 mb-6 sm:mb-8">
              <h3 className="font-semibold text-gray-600 text-sm sm:text-base mb-3">
                Contract & Bank Details
              </h3>
              {[
                {
                  label: "Contract Start",
                  value: formatDate(getVendorField("contractStart")),
                  icon: Calendar,
                },
                {
                  label: "Contract End",
                  value: formatDate(getVendorField("contractEnd")),
                  icon: Calendar,
                },
                {
                  label: "BG Serial Number",
                  value: getVendorField("bgSerialNumber"),
                  icon: FileText,
                },
                {
                  label: "BG Expiry",
                  value: formatDate(getVendorField("bgExpiry")),
                  icon: Shield,
                },
                {
                  label: "Visit Frequency",
                  value: getVendorField("visitFrequency")
                    ? `${getVendorField("visitFrequency")} per year`
                    : "-",
                  icon: RefreshCw,
                },
              ].map((field, index) => (
                <div
                  key={index}
                  className="border-b border-gray-200 pb-3 last:border-b-0"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <field.icon
                        className={`w-5 h-5 text-white p-1 rounded flex-shrink-0 ${
                          field.label === "BG Serial Number"
                            ? "bg-black"
                            : field.label === "Visit Frequency"
                              ? "bg-[#F9971D]"
                              : "bg-[#F94035]"
                        }`}
                      />
                      <span className="text-sm font-medium text-gray-600">
                        {field.label}
                      </span>
                    </div>
                    <span className="text-gray-800 text-right">
                      {field.value || "-"}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            {/* Note */}
            {getVendorField("note") && getVendorField("note") !== "-" && (
              <div className="bg-gray-50 p-6 rounded-lg">
                <div className="flex items-center gap-2 mb-3">
                  <StickyNote className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600" />
                  <h3 className="font-semibold text-sm sm:text-base">Note</h3>
                </div>
                <p className="text-gray-700">{getVendorField("note")}</p>
              </div>
            )}
          </div>
        </div>

        {/* Right Side - Visit Logs */}
        <div className="mb-6 lg:mb-0">
          <div className="bg-gray-50 p-4 sm:p-6 rounded-lg shadow">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-6 gap-3 sm:gap-0">
              <div>
                <h2 className="text-lg sm:text-xl font-bold">Visit Logs</h2>
                <p className="text-gray-600 text-sm sm:text-base">
                  Visit logs of the vendor
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                <button
                  className={`px-4 py-2 border border-[#E4E4E4] rounded-lg shadow-sm flex items-center justify-center gap-1 whitespace-nowrap ${
                    logsLoading || visitLogs.length === 0
                      ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                      : "bg-white text-black hover:bg-gray-50"
                  }`}
                  onClick={handleExportVisitLogs}
                  disabled={logsLoading || visitLogs.length === 0}
                >
                  <Download className="w-4 h-4" />
                  {logsLoading
                    ? "Loading..."
                    : visitLogs.length === 0
                      ? "No Logs to Export"
                      : "Export Logs"}
                </button>
                <button
                  onClick={onAddLog}
                  className="w-full sm:w-auto px-3 sm:px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors flex items-center justify-center gap-2 whitespace-nowrap text-xs sm:text-sm"
                >
                  <Plus className="w-3 h-3 sm:w-4 sm:h-4" />
                  Add Log
                </button>
              </div>
            </div>

            {/* Sort Dropdown */}
            <div className="mb-4">
              <select
                value={sortOrder}
                onChange={handleSortChange}
                className="w-full sm:w-48 p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#897463] focus:border-transparent text-sm sm:text-base"
              >
                <option value="latest">Latest</option>
                <option value="earliest">Earliest</option>
              </select>
            </div>

            <div className="space-y-4 max-h-[645px] overflow-y-auto pr-2">
              {logsLoading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-black mx-auto"></div>
                  <p className="mt-2 text-gray-600 text-sm">
                    Loading visit logs...
                  </p>
                </div>
              ) : logsError ? (
                <div className="text-center py-8 text-red-500">
                  <MessageSquare className="w-8 h-8 sm:w-12 sm:h-12 text-gray-300 mx-auto mb-3" />
                  <p className="font-medium text-sm sm:text-base">
                    Error loading visit logs
                  </p>
                  <p className="text-xs sm:text-sm mt-1">
                    {logsError?.data?.message ||
                      logsError?.error ||
                      "Unable to load visit logs. Please try again."}
                  </p>
                </div>
              ) : sortedLogs && sortedLogs.length > 0 ? (
                sortedLogs.map(
                  (
                    log, // CHANGED: visitLogs to sortedLogs
                  ) => (
                    <div
                      key={log.id}
                      className="bg-white p-4 rounded-lg border border-gray-200 cursor-pointer hover:shadow-md transition-shadow"
                      onClick={() => handleEditLogClick(log)}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <span className="text-sm text-gray-500">
                          {formatDate(log.visitDate)}
                        </span>
                        <span className="text-sm text-gray-500">
                          {formatTime(log.startTime)} -{" "}
                          {formatTime(log.endTime)}
                        </span>
                      </div>
                      <h3 className="font-semibold mb-2">{log.purpose}</h3>
                      <p className="text-gray-600 text-sm line-clamp-2">
                        {log.log || "No description"}
                      </p>
                    </div>
                  ),
                )
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <MessageSquare className="w-8 h-8 sm:w-12 sm:h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-sm sm:text-base">
                    No visit logs found for this vendor.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
const LogForm = ({ vendor, log = null, onSave, onCancel, mode = "add" }) => {
  const [formData, setFormData] = useState({
    purpose: log?.purpose || "",
    visitDate: log?.visitDate
      ? new Date(log.visitDate).toISOString().split("T")[0]
      : "",
    startTime: log?.startTime || "",
    endTime: log?.endTime || "",
    description: log?.log || "",
    supervisorComment: log?.comment || "",
  });

  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Get current user data from Redux
  const selectUserInfo = (state) => state.auth.userInfo || {};
  const getUserEmail = createSelector(
    selectUserInfo,
    (userInfo) => userInfo?.username || userInfo?.email || "",
  );
  const email = useSelector(getUserEmail);
  const { data: userByEmail } = useGetUserByEmailQuery(email);

  // Validation function
  const validateForm = () => {
    const errors = {};

    // Visit Log Validation - ALL REQUIRED
    if (!formData.purpose.trim()) {
      errors.purpose = "Purpose is required";
    }

    if (!formData.visitDate) {
      errors.visitDate = "Visit date is required";
    }

    if (!formData.startTime) {
      errors.startTime = "Start time is required";
    }

    if (!formData.endTime) {
      errors.endTime = "End time is required";
    } else if (formData.startTime && formData.endTime) {
      const start = new Date(`2000-01-01T${formData.startTime}`);
      const end = new Date(`2000-01-01T${formData.endTime}`);
      if (end <= start) {
        errors.endTime = "End time must be after start time";
      }
    }

    if (!formData.description.trim()) {
      errors.description = "Log description is required";
    }

    if (!formData.supervisorComment.trim()) {
      errors.supervisorComment = "Supervisor comment is required";
    }

    return errors;
  };

  const clearFieldError = (fieldName) => {
    setFormErrors((prev) => ({
      ...prev,
      [fieldName]: "",
    }));
  };

  const handleSave = () => {
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setIsSubmitting(true);

    const logData = {
      id: log?.id,
      purpose: formData.purpose,
      visitDate: formData.visitDate,
      startTime: formData.startTime.includes(":")
        ? formData.startTime
        : `${formData.startTime}:00`,
      endTime: formData.endTime.includes(":")
        ? formData.endTime
        : `${formData.endTime}:00`,
      description: formData.description,
      supervisorComment: formData.supervisorComment,
      loggedBy: email,
      loggedByName: userByEmail?.user?.name || "User",
    };

    onSave(vendor.id, logData);

    setIsSubmitting(false);
  };

  const formatDisplayDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date
      .toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      })
      .toUpperCase();
  };

  const formatTime = (timeString) => {
    if (!timeString) return "";
    const [hours, minutes] = timeString.split(":");
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? "PM" : "AM";
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  return (
    <div className=" bg-gray-50 p-4 sm:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Mobile Header - Only show on small screens */}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Side - Form - Always visible */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6 rounded-full">
            {/* Desktop Header - Hidden on mobile */}
            <div className="lg:flex items-center mb-6">
              <button
                onClick={onCancel}
                className="w-9 h-9 flex items-center justify-center hover:bg-gray-100 hover:border-gray-400 transition-all rounded-full"
              >
                <ChevronLeft size={24} className="text-gray-700" />
              </button>
              <div className="flex-1 text-center">
                <h2 className="text-xl font-bold text-gray-900">
                  {mode === "add" ? "Add Log" : "Edit Log"}
                </h2>
                <p className="text-gray-600 mt-1">
                  On the right side is the preview.
                  <br />
                  Fill details on the below
                </p>
              </div>
            </div>

            <div className="space-y-6">
              {/* Purpose */}
              <div>
                <h3 className="font-bold mb-3 text-lg text-gray-900">
                  Purpose *
                </h3>
                <input
                  type="text"
                  className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base ${
                    formErrors.purpose ? "border-red-500" : "border-gray-300"
                  }`}
                  value={formData.purpose}
                  onChange={(e) => {
                    setFormData({ ...formData, purpose: e.target.value });
                    clearFieldError("purpose");
                  }}
                  placeholder="Enter purpose of visit"
                />
                {formErrors.purpose && (
                  <p className="text-red-500 text-sm mt-1">
                    {formErrors.purpose}
                  </p>
                )}
              </div>

              {/* Date and Time */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Visit Date *
                  </label>
                  <input
                    type="date"
                    className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base ${
                      formErrors.visitDate
                        ? "border-red-500"
                        : "border-gray-300"
                    }`}
                    value={formData.visitDate}
                    onChange={(e) => {
                      setFormData({ ...formData, visitDate: e.target.value });
                      clearFieldError("visitDate");
                    }}
                  />
                  {formErrors.visitDate && (
                    <p className="text-red-500 text-sm mt-1">
                      {formErrors.visitDate}
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Start Time *
                    </label>
                    <input
                      type="time"
                      className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base ${
                        formErrors.startTime
                          ? "border-red-500"
                          : "border-gray-300"
                      }`}
                      value={formData.startTime}
                      onChange={(e) => {
                        setFormData({ ...formData, startTime: e.target.value });
                        clearFieldError("startTime");
                      }}
                    />
                    {formErrors.startTime && (
                      <p className="text-red-500 text-sm mt-1">
                        {formErrors.startTime}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      End Time *
                    </label>
                    <input
                      type="time"
                      className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base ${
                        formErrors.endTime
                          ? "border-red-500"
                          : "border-gray-300"
                      }`}
                      value={formData.endTime}
                      onChange={(e) => {
                        setFormData({ ...formData, endTime: e.target.value });
                        clearFieldError("endTime");
                      }}
                    />
                    {formErrors.endTime && (
                      <p className="text-red-500 text-sm mt-1">
                        {formErrors.endTime}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Log Description */}
              <div>
                <label className="block text-sm font-medium mb-1">
                  Log Description *
                </label>
                <textarea
                  className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base ${
                    formErrors.description
                      ? "border-red-500"
                      : "border-gray-300"
                  }`}
                  rows="6"
                  value={formData.description}
                  onChange={(e) => {
                    setFormData({ ...formData, description: e.target.value });
                    clearFieldError("description");
                  }}
                  placeholder="Describe the visit details..."
                />
                {formErrors.description && (
                  <p className="text-red-500 text-sm mt-1">
                    {formErrors.description}
                  </p>
                )}
              </div>

              {/* Supervisor Comment */}
              <div>
                <label className="block text-sm font-medium mb-1">
                  Supervisor Comment *
                </label>
                <textarea
                  className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base ${
                    formErrors.supervisorComment
                      ? "border-red-500"
                      : "border-gray-300"
                  }`}
                  rows="3"
                  value={formData.supervisorComment}
                  onChange={(e) => {
                    setFormData({
                      ...formData,
                      supervisorComment: e.target.value,
                    });
                    clearFieldError("supervisorComment");
                  }}
                  placeholder="Add supervisor comments..."
                />
                {formErrors.supervisorComment && (
                  <p className="text-red-500 text-sm mt-1">
                    {formErrors.supervisorComment}
                  </p>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 pt-4">
                <button
                  className="flex-1 px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium text-sm sm:text-base"
                  onClick={onCancel}
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  className="flex-1 px-6 py-3 bg-black text-white rounded-lg shadow-sm hover:bg-gray-800 transition-colors disabled:opacity-50 font-medium text-sm sm:text-base flex items-center justify-center gap-2"
                  onClick={handleSave}
                  disabled={isSubmitting}
                  style={{
                    backgroundColor: "rgb(48 88 69 / var(--tw-bg-opacity, 1))",
                  }}
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      {mode === "add" ? "Adding..." : "Updating..."}
                    </>
                  ) : mode === "add" ? (
                    "Add Log"
                  ) : (
                    "Update Log"
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Right Side - Preview - Hidden on mobile, visible on desktop */}
          <div className="hidden lg:block bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            {/* Header with "Logged by You" */}
            <div className="bg-gray-100 border-b-2 border-gray-200 pb-4 mb-6 -mx-6 -mt-6 px-6 pt-6 rounded-t-xl">
              <div className="flex items-center justify-center">
                <span className="font-medium text-gray-700">
                  <span className="text-gray-600">Logged by</span>
                  <span className="text-blue-500"> You</span>
                </span>
              </div>
            </div>

            {/* Purpose and Time */}
            <div className="flex justify-between items-start mb-4 gap-4">
              <h3 className="text-2xl font-bold text-gray-900 flex-1 break-words min-w-0">
                {formData.purpose || "Purpose will appear here"}
              </h3>
              <p className="text-gray-600 whitespace-nowrap flex-shrink-0 text-sm">
                {formData.startTime && formData.endTime
                  ? `${formatTime(formData.startTime)} – ${formatTime(
                      formData.endTime,
                    )}`
                  : "Time will appear here"}
              </p>
            </div>

            {/* Date */}
            <p className="text-gray-600 mb-6 text-sm">
              {formData.visitDate
                ? formatDisplayDate(formData.visitDate)
                : "Date will appear here"}
            </p>

            {/* Description */}
            <div className="space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <p className="text-gray-700 whitespace-pre-wrap text-sm leading-relaxed">
                  {formData.description ||
                    "Log description will appear here..."}
                </p>
              </div>

              {/* Supervisor Comment */}
              {formData.supervisorComment && (
                <div className="mt-6">
                  <p className="font-semibold text-gray-600 mb-2 text-sm">
                    Supervisor Comment:
                  </p>
                  <p className="text-gray-700 bg-gray-50 p-3 rounded-lg border border-gray-200 text-sm">
                    {formData.supervisorComment}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
// Vendor Form Component (Add/Edit Vendor)
const VendorMngt = ({
  onAddVendor,
  onCancel,
  categories = [],
  editVendor = null,
  academyId,
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [vendorIdentityExists, setVendorIdentityExists] = useState(false);
  const [checkingVendorIdentity, setCheckingVendorIdentity] = useState(false);
  const [vendorIdentityTouched, setVendorIdentityTouched] = useState(false);

  // FIXED: Use the query hook with academyId parameter
  const [checkVendorIdentityMutation] = useCheckVendorIdentityMutation();

  const [newVendor, setNewVendor] = useState(() => {
    if (editVendor) {
      let currentCategory = "";
      if (editVendor.vendorCategory && editVendor.vendorCategory.title) {
        currentCategory = editVendor.vendorCategory.title;
      } else if (editVendor.category) {
        currentCategory = editVendor.category;
      } else if (editVendor.vendorCategoryId) {
        const category = categories.find(
          (cat) => cat.id === editVendor.vendorCategoryId,
        );
        currentCategory = category ? category.title : "";
      }

      return {
        id: editVendor.id,
        name: editVendor.name || "",
        vendorIdentity: editVendor.vendorIdentity || "",
        address: editVendor.address || "",
        category: currentCategory,
        contractStart:
          editVendor.contractStartDate || editVendor.contractStart || "",
        contractEnd: editVendor.contractEndDate || editVendor.contractEnd || "",
        contactPerson:
          editVendor.contactPersonName || editVendor.contactPerson || "",
        phoneNumber:
          editVendor.contactPersonPhoneNumber || editVendor.phoneNumber || "",
        email: editVendor.contactPersonEmail || editVendor.email || "",
        bgSerialNumber: editVendor.bgSerialNumber || "",
        bgExpiry: editVendor.bgExpiryDate || editVendor.bgExpiry || "",
        visitFrequency:
          editVendor.visitFrequencyPerYear?.toString() ||
          editVendor.visitFrequency ||
          "",
        note: editVendor.note || "",
      };
    }
    return {
      name: "",
      vendorIdentity: "",
      address: "",
      category: "",
      contractStart: "",
      contractEnd: "",
      contactPerson: "",
      phoneNumber: "",
      email: "",
      bgSerialNumber: "",
      bgExpiry: "",
      visitFrequency: "",
      note: "",
    };
  });

  // Get vendors data to check for duplicates
  const { data: vendors = [] } = useGetVendorsByAcademyQuery(academyId, {
    skip: !academyId,
  });
  // Custom function to check vendor identity uniqueness
  const checkVendorIdentityUnique = (vendorIdentity) => {
    if (!vendorIdentity || vendorIdentity.trim().length < 3) {
      return true;
    }

    // Skip check if we're editing and the vendor identity hasn't changed
    if (editVendor && editVendor.vendorIdentity === vendorIdentity) {
      return true;
    }

    // Check if any other vendor in the same academy has this identity
    const existingVendor = vendors.find(
      (vendor) =>
        vendor.vendorIdentity === vendorIdentity &&
        vendor.id !== (editVendor?.id || null),
    );

    return !existingVendor;
  };

  const handleVendorIdentityChange = async (value) => {
    setNewVendor({ ...newVendor, vendorIdentity: value });
    setVendorIdentityTouched(true);
    clearFieldError("vendorIdentity");

    // Clear existing status when user starts typing
    if (vendorIdentityExists) {
      setVendorIdentityExists(false);
    }

    // Immediate validation for required field
    if (!value.trim()) {
      setFormErrors((prev) => ({
        ...prev,
        vendorIdentity: "Vendor identity (TPN) is required",
      }));
      return;
    }

    // Clear any previous errors if basic validation passes
    clearFieldError("vendorIdentity");

    // Check uniqueness with a short debounce
    const timeoutId = setTimeout(async () => {
      const isUnique = checkVendorIdentityUnique(value);
      setVendorIdentityExists(!isUnique);

      if (!isUnique) {
        setFormErrors((prev) => ({
          ...prev,
          vendorIdentity: `Vendor identity '${value}' is already registered in this academy. Please use a different identity.`,
        }));
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  };

  const validateStep1 = () => {
    const errors = {};

    if (!newVendor.name.trim()) {
      errors.name = "Vendor name is required";
    } else if (newVendor.name.trim().length < 2) {
      errors.name = "Vendor name must be at least 2 characters";
    }

    if (!newVendor.vendorIdentity.trim()) {
      errors.vendorIdentity = "Vendor identity (TPN) is required";
    } else if (vendorIdentityExists) {
      errors.vendorIdentity = `Vendor identity '${newVendor.vendorIdentity}' is already registered in this academy. Please use a different identity.`;
    }

    if (!newVendor.address.trim()) {
      errors.address = "Address is required";
    }

    if (!newVendor.category) {
      errors.category = "Category is required";
    }

    if (!newVendor.visitFrequency) {
      errors.visitFrequency = "Visit frequency is required";
    } else if (
      isNaN(newVendor.visitFrequency) ||
      parseInt(newVendor.visitFrequency) <= 0
    ) {
      errors.visitFrequency = "Visit frequency must be a positive number";
    }

    if (!newVendor.contractStart) {
      errors.contractStart = "Contract start date is required";
    }

    if (!newVendor.contractEnd) {
      errors.contractEnd = "Contract end date is required";
    } else if (newVendor.contractStart && newVendor.contractEnd) {
      const startDate = new Date(newVendor.contractStart);
      const endDate = new Date(newVendor.contractEnd);
      if (endDate < startDate) {
        errors.contractEnd = "Contract end date cannot be before start date";
      }
    }

    return errors;
  };

  const validateStep2 = () => {
    const errors = {};

    if (!newVendor.contactPerson.trim()) {
      errors.contactPerson = "Contact person name is required";
    } else if (newVendor.contactPerson.trim().length < 2) {
      errors.contactPerson =
        "Contact person name must be at least 2 characters";
    }

    if (!newVendor.phoneNumber.trim()) {
      errors.phoneNumber = "Phone number is required";
    } else {
      const phoneRegex = /^[+]?[0-9\s\-()]{8,}$/;
      if (!phoneRegex.test(newVendor.phoneNumber)) {
        errors.phoneNumber = "Please enter a valid phone number";
      }
    }

    if (!newVendor.email.trim()) {
      errors.email = "Email address is required";
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(newVendor.email)) {
        errors.email = "Please enter a valid email address";
      }
    }

    if (!newVendor.note.trim()) {
      errors.note = "Note is required";
    } else if (newVendor.note.length > 500) {
      errors.note = "Note cannot exceed 500 characters";
    }

    return errors;
  };

  const clearFieldError = (fieldName) => {
    setFormErrors((prev) => ({
      ...prev,
      [fieldName]: "",
    }));
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch (error) {
      return dateString;
    }
  };

  const handleAddVendor = async () => {
    setIsSubmitting(true);

    // Final validation check
    const step1Errors = validateStep1();
    const step2Errors = validateStep2();
    const allErrors = { ...step1Errors, ...step2Errors };

    if (Object.keys(allErrors).length > 0) {
      setFormErrors(allErrors);
      setIsSubmitting(false);
      if (Object.keys(step1Errors).length > 0) {
        setCurrentStep(1);
      }
      return;
    }

    try {
      await onAddVendor(newVendor);

      if (!editVendor) {
        setNewVendor({
          name: "",
          vendorIdentity: "",
          address: "",
          category: "",
          contractStart: "",
          contractEnd: "",
          contactPerson: "",
          phoneNumber: "",
          email: "",
          bgSerialNumber: "",
          bgExpiry: "",
          visitFrequency: "",
          note: "",
        });
        setFormErrors({});
        setVendorIdentityExists(false);
        setVendorIdentityTouched(false);
        setCurrentStep(1);
      }
    } catch (error) {
      console.error("Error in vendor form:", error);
      // Handle specific vendor identity error from backend
      if (
        error.data?.message?.includes("Vendor identity") ||
        error.data?.message?.includes("already exists")
      ) {
        setFormErrors((prev) => ({
          ...prev,
          vendorIdentity: error.data.message,
        }));
        setCurrentStep(1);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const nextStep = async () => {
    // Validate step 1 including vendor identity
    const errors = validateStep1();

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setFormErrors({});
    setCurrentStep(currentStep + 1);
  };

  const prevStep = () => {
    setFormErrors({});
    setCurrentStep(currentStep - 1);
  };

  const renderStep1 = () => (
    <div className="space-y-4">
      <div>
        <h3 className="font-bold mb-3 text-lg text-center">
          Company Details *
        </h3>
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium mb-1">Name *</label>
            <input
              type="text"
              className={`w-full p-2 border rounded-lg focus:ring-2 focus:ring-[#897463] focus:border-transparent ${
                formErrors.name ? "border-red-500" : "border-gray-300"
              }`}
              value={newVendor.name}
              onChange={(e) => {
                setNewVendor({ ...newVendor, name: e.target.value });
                clearFieldError("name");
              }}
              placeholder="ABC Inc."
            />
            {formErrors.name && (
              <p className="text-red-500 text-sm mt-1">{formErrors.name}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">
              Vendor Identity (TPN) *
            </label>
            <div className="relative">
              <input
                type="text"
                className={`w-full p-2 border rounded-lg focus:ring-2 focus:ring-[#897463] focus:border-transparent ${
                  formErrors.vendorIdentity
                    ? "border-red-500"
                    : vendorIdentityExists
                      ? "border-orange-500"
                      : newVendor.vendorIdentity.trim().length >= 3 &&
                          !vendorIdentityExists &&
                          !checkingVendorIdentity
                        ? "border-green-500"
                        : "border-gray-300"
                } ${checkingVendorIdentity ? "pr-10" : ""}`}
                value={newVendor.vendorIdentity}
                onChange={(e) => handleVendorIdentityChange(e.target.value)}
                onBlur={() => setVendorIdentityTouched(true)}
                placeholder="TPN-12345"
              />
              {checkingVendorIdentity && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400"></div>
                </div>
              )}
              {!checkingVendorIdentity &&
                newVendor.vendorIdentity.trim().length >= 3 && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    {vendorIdentityExists ? (
                      <svg
                        className="h-4 w-4 text-orange-500"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                          clipRule="evenodd"
                        />
                      </svg>
                    ) : (
                      <svg
                        className="h-4 w-4 text-green-500"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    )}
                  </div>
                )}
            </div>

            {/* IMMEDIATE ERROR MESSAGES - Show immediately as user types */}
            {formErrors.vendorIdentity && (
              <p className="text-red-500 text-sm mt-1">
                {formErrors.vendorIdentity}
              </p>
            )}

            {/* IMMEDIATE STATUS MESSAGES */}
            {vendorIdentityTouched &&
              newVendor.vendorIdentity.trim() &&
              !formErrors.vendorIdentity && (
                <div className="mt-1">
                  {checkingVendorIdentity && (
                    <p className="text-blue-500 text-sm">
                      Checking vendor identity availability...
                    </p>
                  )}
                  {!checkingVendorIdentity && vendorIdentityExists && (
                    <p className="text-orange-500 text-sm">
                      Vendor identity '{newVendor.vendorIdentity}' is already
                      registered in this academy. Please use a different
                      identity.
                    </p>
                  )}
                  {!checkingVendorIdentity &&
                    !vendorIdentityExists &&
                    newVendor.vendorIdentity.trim().length >= 3 && (
                      <p className="text-green-500 text-sm">
                        Vendor identity (TPN) is available for this academy.
                      </p>
                    )}
                </div>
              )}
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Address *</label>
            <input
              type="text"
              className={`w-full p-2 border rounded-lg focus:ring-2 focus:ring-[#897463] focus:border-transparent ${
                formErrors.address ? "border-red-500" : "border-gray-300"
              }`}
              value={newVendor.address}
              onChange={(e) => {
                setNewVendor({ ...newVendor, address: e.target.value });
                clearFieldError("address");
              }}
              placeholder="123 Bazaar, Changlam, Thimphu"
            />
            {formErrors.address && (
              <p className="text-red-500 text-sm mt-1">{formErrors.address}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Category *</label>
            <select
              className={`w-full p-2 border rounded-lg focus:ring-2 focus:ring-[#897463] focus:border-transparent ${
                formErrors.category ? "border-red-500" : "border-gray-300"
              }`}
              value={newVendor.category}
              onChange={(e) => {
                setNewVendor({ ...newVendor, category: e.target.value });
                clearFieldError("category");
              }}
            >
              <option value="">Select Category</option>
              {categories.map((category) => (
                <option key={category.id} value={category.title}>
                  {category.title}
                </option>
              ))}
            </select>
            {formErrors.category && (
              <p className="text-red-500 text-sm mt-1">{formErrors.category}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">
              Visit Frequency (per year) *
            </label>
            <input
              type="number"
              className={`w-full p-2 border rounded-lg focus:ring-2 focus:ring-[#897463] focus:border-transparent ${
                formErrors.visitFrequency ? "border-red-500" : "border-gray-300"
              }`}
              value={newVendor.visitFrequency}
              onChange={(e) => {
                setNewVendor({ ...newVendor, visitFrequency: e.target.value });
                clearFieldError("visitFrequency");
              }}
              placeholder="4"
              min="1"
            />
            {formErrors.visitFrequency && (
              <p className="text-red-500 text-sm mt-1">
                {formErrors.visitFrequency}
              </p>
            )}
          </div>
        </div>
      </div>

      <div>
        <h3 className="font-bold mb-3 text-lg text-center">
          Contract Details *
        </h3>
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium mb-1">
              Contract Start *
            </label>
            <input
              type="date"
              className={`w-full p-2 border rounded-lg focus:ring-2 focus:ring-[#897463] focus:border-transparent ${
                formErrors.contractStart ? "border-red-500" : "border-gray-300"
              }`}
              value={newVendor.contractStart}
              onChange={(e) => {
                setNewVendor({ ...newVendor, contractStart: e.target.value });
                clearFieldError("contractStart");
              }}
            />
            {formErrors.contractStart && (
              <p className="text-red-500 text-sm mt-1">
                {formErrors.contractStart}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">
              Contract End *
            </label>
            <input
              type="date"
              className={`w-full p-2 border rounded-lg focus:ring-2 focus:ring-[#897463] focus:border-transparent ${
                formErrors.contractEnd ? "border-red-500" : "border-gray-300"
              }`}
              value={newVendor.contractEnd}
              onChange={(e) => {
                setNewVendor({ ...newVendor, contractEnd: e.target.value });
                clearFieldError("contractEnd");
              }}
            />
            {formErrors.contractEnd && (
              <p className="text-red-500 text-sm mt-1">
                {formErrors.contractEnd}
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="flex gap-3 pt-3 justify-center">
        <button
          className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          Cancel
        </button>
        <button
          className="px-4 py-2 bg-black text-white rounded-lg shadow-sm hover:bg-gray-800 transition-colors disabled:opacity-50 flex items-center gap-2"
          onClick={nextStep}
          disabled={
            isSubmitting || vendorIdentityExists || checkingVendorIdentity
          }
          style={{
            backgroundColor: "rgb(48 88 69 / var(--tw-bg-opacity, 1))",
          }}
        >
          {isSubmitting ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              Validating...
            </>
          ) : (
            "Next"
          )}
        </button>
      </div>
    </div>
  );

  // Step 2: Bank & Contact Details - ALL FIELDS REQUIRED
  const renderStep2 = () => (
    <div className="space-y-4">
      <div className="border-b pb-4">
        <h3 className="font-bold mb-3 text-lg text-center">Bank Guarantee</h3>
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium mb-1">
              BG Serial Number
            </label>
            <input
              type="text"
              className={`w-full p-2 border rounded-lg focus:ring-2 focus:ring-[#897463] focus:border-transparent ${
                formErrors.bgSerialNumber ? "border-red-500" : "border-gray-300"
              }`}
              value={newVendor.bgSerialNumber}
              onChange={(e) => {
                setNewVendor({ ...newVendor, bgSerialNumber: e.target.value });
                clearFieldError("bgSerialNumber");
              }}
              placeholder="BG123456789"
            />
            {formErrors.bgSerialNumber && (
              <p className="text-red-500 text-sm mt-1">
                {formErrors.bgSerialNumber}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">
              Expiry Date
            </label>
            <input
              type="date"
              className={`w-full p-2 border rounded-lg focus:ring-2 focus:ring-[#897463] focus:border-transparent ${
                formErrors.bgExpiry ? "border-red-500" : "border-gray-300"
              }`}
              value={newVendor.bgExpiry}
              onChange={(e) => {
                setNewVendor({ ...newVendor, bgExpiry: e.target.value });
                clearFieldError("bgExpiry");
              }}
            />
            {formErrors.bgExpiry && (
              <p className="text-red-500 text-sm mt-1">{formErrors.bgExpiry}</p>
            )}
          </div>
        </div>
      </div>

      <div className="border-b pb-4">
        <h3 className="font-bold mb-3 text-lg text-center">
          Contact Person Details *
        </h3>
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium mb-1">
              Contact Person *
            </label>
            <input
              type="text"
              className={`w-full p-2 border rounded-lg focus:ring-2 focus:ring-[#897463] focus:border-transparent ${
                formErrors.contactPerson ? "border-red-500" : "border-gray-300"
              }`}
              value={newVendor.contactPerson}
              onChange={(e) => {
                setNewVendor({ ...newVendor, contactPerson: e.target.value });
                clearFieldError("contactPerson");
              }}
              placeholder="Name..."
            />
            {formErrors.contactPerson && (
              <p className="text-red-500 text-sm mt-1">
                {formErrors.contactPerson}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">
              Phone Number *
            </label>
            <input
              type="text"
              className={`w-full p-2 border rounded-lg focus:ring-2 focus:ring-[#897463] focus:border-transparent ${
                formErrors.phoneNumber ? "border-red-500" : "border-gray-300"
              }`}
              value={newVendor.phoneNumber}
              onChange={(e) => {
                setNewVendor({ ...newVendor, phoneNumber: e.target.value });
                clearFieldError("phoneNumber");
              }}
              placeholder="+975-17123456"
            />
            {formErrors.phoneNumber && (
              <p className="text-red-500 text-sm mt-1">
                {formErrors.phoneNumber}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Email *</label>
            <input
              type="email"
              className={`w-full p-2 border rounded-lg focus:ring-2 focus:ring-[#897463] focus:border-transparent ${
                formErrors.email ? "border-red-500" : "border-gray-300"
              }`}
              value={newVendor.email}
              onChange={(e) => {
                setNewVendor({ ...newVendor, email: e.target.value });
                clearFieldError("email");
              }}
              placeholder="tshering@example.com"
            />
            {formErrors.email && (
              <p className="text-red-500 text-sm mt-1">{formErrors.email}</p>
            )}
          </div>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium mb-1">Note *</label>
        <textarea
          className={`w-full p-2 border rounded-lg focus:ring-2 focus:ring-[#897463] focus:border-transparent ${
            formErrors.note ? "border-red-500" : "border-gray-300"
          }`}
          value={newVendor.note}
          onChange={(e) => {
            setNewVendor({ ...newVendor, note: e.target.value });
            clearFieldError("note");
          }}
          placeholder="Specializes in construction and maintenance works"
          rows="3"
        />
        <div className="flex justify-between text-xs text-gray-500 mt-1">
          <span>
            {formErrors.note ? (
              <span className="text-red-500">{formErrors.note}</span>
            ) : (
              "Required field"
            )}
          </span>
          <span>{newVendor.note.length}/500 characters</span>
        </div>
      </div>

      <div className="flex gap-3 pt-3 justify-center">
        <button
          className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
          onClick={prevStep}
          disabled={isSubmitting}
        >
          Previous
        </button>
        <button
          className="px-4 py-2 bg-black text-white rounded-lg shadow-sm hover:bg-gray-800 transition-colors disabled:opacity-50 flex items-center gap-2"
          onClick={handleAddVendor}
          disabled={isSubmitting}
          style={{
            backgroundColor: "rgb(48 88 69 / var(--tw-bg-opacity, 1))",
          }}
        >
          {isSubmitting ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              {editVendor ? "Updating..." : "Adding..."}
            </>
          ) : editVendor ? (
            "Update Vendor"
          ) : (
            "Add Vendor"
          )}
        </button>
      </div>
    </div>
  );

  const previewFields = {
    company: [
      { key: "name", label: "Name", value: newVendor.name, icon: Building },
      {
        key: "vendorIdentity",
        label: "Vendor Identity (TPN)",
        value: newVendor.vendorIdentity,
        icon: FileText,
      },
      {
        key: "address",
        label: "Address",
        value: newVendor.address,
        icon: MapPin,
      },
      {
        key: "category",
        label: "Category",
        value: newVendor.category,
        icon: Folder,
      },
      {
        key: "visitFrequency",
        label: "Visit Frequency",
        value: newVendor.visitFrequency
          ? `${newVendor.visitFrequency} per year`
          : "",
        icon: RefreshCw,
      },
    ],
    contact: [
      {
        key: "contactPerson",
        label: "Contact Person",
        value: newVendor.contactPerson,
        icon: User,
      },
      {
        key: "phoneNumber",
        label: "Phone Number",
        value: newVendor.phoneNumber,
        icon: Phone,
      },
      { key: "email", label: "Email", value: newVendor.email, icon: Mail },
    ],
    contract: [
      {
        key: "contractStart",
        label: "Contract Start",
        value: formatDate(newVendor.contractStart),
        icon: Calendar,
      },
      {
        key: "contractEnd",
        label: "Contract End",
        value: formatDate(newVendor.contractEnd),
        icon: Calendar,
      },
      {
        key: "bgSerialNumber",
        label: "BG Serial Number",
        value: newVendor.bgSerialNumber,
        icon: FileText,
      },
      {
        key: "bgExpiry",
        label: "BG Expiry",
        value: formatDate(newVendor.bgExpiry),
        icon: Shield,
      },
    ],
    note: [
      { key: "note", label: "Note", value: newVendor.note, icon: StickyNote },
    ],
  };

  const renderPreviewField = (field) => {
    const IconComponent = field.icon;
    return (
      <div
        key={field.key}
        className="border-b border-gray-200 py-2 last:border-b-0"
      >
        <div className="flex items-center">
          <IconComponent className="mr-2 h-4 w-4 text-gray-600 flex-shrink-0" />
          <div className="flex justify-between items-center flex-1 min-w-0">
            <span
              className={`${
                field.value ? "text-gray-800" : "text-gray-400"
              } text-sm font-medium truncate`}
            >
              {field.label}:
            </span>
            <span
              className={`${
                field.value ? "text-gray-800" : "text-gray-400"
              } text-sm ml-2 truncate text-right flex-1 max-w-[60%]`}
            >
              {field.value || "-"}
            </span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Side - Form - Always visible */}
        <div className="bg-gray-50 p-4 rounded-lg">
          {/* Header with back button and centered title */}
          <div className="flex items-center mb-4">
            <button
              onClick={onCancel}
              className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-gray-100 hover:border-gray-400 transition-all"
              disabled={isSubmitting}
            >
              <ChevronLeft
                size={24}
                strokeWidth={2}
                className="text-gray-700"
              />
            </button>
            <div className="flex-1 text-center">
              <h2 className="text-lg font-bold">
                {editVendor ? "Edit Vendor" : "Add Vendor"}
              </h2>
              <p className="text-gray-600 text-sm mt-1">
                On the right side is the preview.
                <br />
                Fill details on the below
              </p>
              {/* Progress Rectangles */}
              <div className="flex justify-center gap-3 mt-2">
                <div
                  className={`w-10 h-1.5 rounded-sm ${
                    currentStep >= 1 ? "bg-blue-600" : "bg-gray-300"
                  }`}
                ></div>
                <div
                  className={`w-10 h-1.5 rounded-sm ${
                    currentStep >= 2 ? "bg-blue-600" : "bg-gray-300"
                  }`}
                ></div>
              </div>
            </div>
          </div>

          {currentStep === 1 && renderStep1()}
          {currentStep === 2 && renderStep2()}
        </div>

        {/* Right Side - Preview - Hidden on mobile, visible on desktop */}
        <div className="hidden lg:block bg-white p-6 rounded-lg shadow border">
          <div className="mb-4">
            <h2 className="text-xl font-bold">Vendor Preview</h2>
            <p className="text-gray-600">Preview of vendor information</p>
            {editVendor && (
              <p className="text-sm text-green-600 mt-1">
                Editing: {editVendor.name}
              </p>
            )}
          </div>
          <div className="space-y-4">
            {/* Company Details Group */}
            <div className="bg-gray-50 p-4 rounded-lg">
              {previewFields.company.map(renderPreviewField)}
            </div>

            {/* Contact Details Group */}
            <div className="bg-gray-50 p-4 rounded-lg">
              {previewFields.contact.map(renderPreviewField)}
            </div>

            {/* Contract & Bank Details Group */}
            <div className="bg-gray-50 p-4 rounded-lg">
              {previewFields.contract.map(renderPreviewField)}
            </div>
          </div>

          {newVendor.note && (
            <div className="bg-gray-50 p-4 rounded-lg mt-4">
              {previewFields.note.map(renderPreviewField)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
export default VendorManagement;
